import { useCallback, useEffect, useMemo, useRef, useState, type MouseEvent } from "react";
import { Check, ChevronDown, MoreVertical, Search, Settings2, ShieldCheck, X } from "lucide-react";
import { usePage, type SectionProps } from "./context";
import { Band, Head, cn, rv, str } from "./ui";
import { useAuth } from "./auth";
import { fieldsApi, type ContactFieldDef, type FieldType } from "./contacts-api";
import { fieldTypes, fieldVisibility, typeOf, visOf } from "./fields-meta";
import { navLabel, settingsNav, settingsNavAnchors } from "./settings-nav";
import { apiErrorText } from "@/lib/api";
import { goAnchor } from "./app-nav";
import { t, locale } from "@/lib/i18n";

/* «Поля контактов»: встроенные и свои поля карточки контакта с типами и видимостью — с сервера (/api/fields).
   Заводят, меняют, дублируют и удаляют поля владелец и администраторы; остальные только смотрят.
   Встроенное поле можно переименовать и скрыть, но не удалить и не сменить тип; ID поля после создания не меняется. */

type FieldT = ContactFieldDef;
type Visibility = ContactFieldDef["visibility"];
type SortKey = "label" | "key" | "type" | "added";
type Draft = { id: string | null; label: string; key: string; description: string; type: FieldType; visibility: Visibility; options: string };

const EMPTY_DRAFT: Draft = { id: null, label: "", key: "", description: "", type: "text", visibility: "always", options: "" };
const MONTHS = [t("янв"), t("фев"), t("мар"), t("апр"), t("мая"), t("июн"), t("июл"), t("авг"), t("сен"), t("окт"), t("ноя"), t("дек")];
const FIELD =
  "h-10 w-full box-border min-w-0 rounded-btn border border-border bg-background px-3 text-[13px] text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/25";
// как на сервере (backend/hub/api/fields.py KEY)
const KEY_RE = /^[a-zA-Z][a-zA-Z0-9_]{0,39}$/;

function formatDate(iso: string) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

// русские названия превращаем в латинский ID поля: «Город доставки» → gorodDostavki
const TRANSLIT: Record<string, string> = {
  а: "a", б: "b", в: "v", г: "g", д: "d", е: "e", ё: "e", ж: "zh", з: "z", и: "i", й: "y", к: "k", л: "l", м: "m",
  н: "n", о: "o", п: "p", р: "r", с: "s", т: "t", у: "u", ф: "f", х: "h", ц: "ts", ч: "ch", ш: "sh", щ: "sch",
  ъ: "", ы: "y", ь: "", э: "e", ю: "yu", я: "ya",
};

const slug = (v: string) => {
  const words = v.trim().toLowerCase().split(/[^a-zа-яё0-9]+/i).filter(Boolean);
  const joined = words
    .map((w, i) => {
      const latin = Array.from(w).map((c) => TRANSLIT[c] ?? c).join("");
      return i === 0 ? latin : latin.charAt(0).toUpperCase() + latin.slice(1);
    })
    .join("");
  const clean = joined.replace(/[^a-zA-Z0-9]/g, "").slice(0, 30);
  return /^[a-z]/.test(clean) ? clean : clean ? `f${clean}` : "";
};

const splitOptions = (v: string) => v.split(",").map((o) => o.trim()).filter(Boolean);

function FieldsDemo({ title, subtitle }: SectionProps) {
  const { brand } = usePage();
  const { user } = useAuth();
  const canEdit = user?.role === "owner" || user?.role === "admin";
  const [items, setItems] = useState<FieldT[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState<{ key: SortKey | null; dir: number }>({ key: null, dir: 1 });
  const [showDesc, setShowDesc] = useState(true);
  const [dense, setDense] = useState(false);
  const [viewMenu, setViewMenu] = useState(false);
  const [menu, setMenu] = useState<{ id: string; top: number; left: number } | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [keyTouched, setKeyTouched] = useState(false);
  const [perPage, setPerPage] = useState(25);
  const [page, setPage] = useState(1);
  const [toast, setToast] = useState("");
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => () => clearTimeout(timer.current), []);

  const flash = useCallback((text: string) => {
    setToast(text);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setToast(""), 4200);
  }, []);

  useEffect(() => {
    fieldsApi
      .list()
      .then(setItems)
      .catch((err) => flash(apiErrorText(err)))
      .finally(() => setLoaded(true));
  }, [flash]);

  useEffect(() => {
    const onDown = (e: PointerEvent) => {
      if ((e.target as HTMLElement | null)?.closest?.("[data-menu-root]")) return;
      setMenu(null);
      setViewMenu(false);
    };
    document.addEventListener("pointerdown", onDown);
    return () => document.removeEventListener("pointerdown", onDown);
  }, []);

  useEffect(() => {
    if (!menu) return;
    const close = () => setMenu(null);
    window.addEventListener("scroll", close, true);
    return () => window.removeEventListener("scroll", close, true);
  }, [menu]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = items.filter((f) => {
      if (!q) return true;
      return `${f.label} ${f.key} ${f.description} ${typeOf(f.type).label}`.toLowerCase().includes(q);
    });
    if (!sort.key) return list;
    const key = sort.key;
    const value = (f: FieldT) => (key === "type" ? typeOf(f.type).label : key === "added" ? f.createdAt : f[key]);
    return [...list].sort((a, b) => value(a).localeCompare(value(b), locale) * sort.dir);
  }, [items, query, sort]);

  const pages = Math.max(1, Math.ceil(visible.length / perPage));
  const current = Math.min(page, pages);
  const start = (current - 1) * perPage;
  const rows = visible.slice(start, start + perPage);
  const range = visible.length === 0 ? t("0 из 0") : t("{from}–{to} из {total}", { from: start + 1, to: start + rows.length, total: visible.length });

  const toggleSort = (key: SortKey) =>
    setSort((s) => (s.key === key ? { key, dir: s.dir === 1 ? -1 : 1 } : { key, dir: 1 }));

  const openAdd = () => {
    setDraft({ ...EMPTY_DRAFT });
    setKeyTouched(false);
    setMenu(null);
    setViewMenu(false);
  };

  const openEdit = (f: FieldT) => {
    if (!canEdit) return;
    setDraft({
      id: f.id,
      label: f.label,
      key: f.key,
      description: f.description,
      type: f.type,
      visibility: f.visibility,
      options: f.options.join(", "),
    });
    setKeyTouched(true);
    setMenu(null);
  };

  const keyError = (() => {
    if (!draft || draft.id) return "";
    const k = draft.key.trim();
    if (!k) return "";
    if (!KEY_RE.test(k)) return t("Латиница, цифры и _, начинается с буквы, без пробелов: phone, orderId");
    if (items.some((f) => f.key.toLowerCase() === k.toLowerCase())) return t("Такой ID уже занят — выберите другой");
    return "";
  })();

  // пустой ID при создании — сервер сделает его из названия сам
  const canSave = Boolean(draft && draft.label.trim().length >= 1 && !keyError && !busy);

  const save = async () => {
    if (!draft || !canSave) return;
    const data = {
      label: draft.label.trim(),
      description: draft.description.trim(),
      type: draft.type,
      visibility: draft.visibility,
      options: draft.type === "multi" ? splitOptions(draft.options) : [],
    };
    setBusy(true);
    try {
      if (draft.id) {
        const was = items.find((f) => f.id === draft.id);
        // тип встроенного поля сервер менять не даёт — не отправляем его
        const f = await fieldsApi.update(draft.id, was?.system ? { ...data, type: undefined } : data);
        setItems((prev) => prev.map((x) => (x.id === f.id ? f : x)));
        flash(t("Поле «{label}» обновлено — карточки контактов уже показывают новый вид", { label: f.label }));
      } else {
        const f = await fieldsApi.create({ ...data, key: draft.key.trim() || undefined });
        setItems((prev) => [f, ...prev]);
        setPage(1);
        flash(t("Поле «{label}» добавлено в карточку контакта — ID {key}", { label: f.label, key: f.key }));
      }
      setDraft(null);
    } catch (err) {
      flash(apiErrorText(err));
    } finally {
      setBusy(false);
    }
  };

  const duplicate = async (f: FieldT) => {
    let n = 2;
    let key = `${f.key}${n}`;
    while (items.some((x) => x.key.toLowerCase() === key.toLowerCase())) {
      n += 1;
      key = `${f.key}${n}`;
    }
    setMenu(null);
    try {
      const copy = await fieldsApi.create({
        key,
        label: t("{label} (копия)", { label: f.label }).slice(0, 60),
        description: f.description,
        type: f.type,
        visibility: f.visibility,
        options: f.options,
      });
      setItems((prev) => {
        const i = prev.findIndex((x) => x.id === f.id);
        const next = [...prev];
        next.splice(i + 1, 0, copy);
        return next;
      });
      flash(t("Копия поля «{label}» создана — ID {key}", { label: f.label, key: copy.key }));
    } catch (err) {
      flash(apiErrorText(err));
    }
  };

  const cycleVisibility = async (f: FieldT) => {
    const order = fieldVisibility.map((v) => v.id);
    const next = order[(order.indexOf(f.visibility) + 1) % order.length];
    setMenu(null);
    try {
      const saved = await fieldsApi.update(f.id, { visibility: next });
      setItems((prev) => prev.map((x) => (x.id === f.id ? saved : x)));
      flash(t("«{label}»: {vis}", { label: f.label, vis: visOf(next).label.toLowerCase() }));
    } catch (err) {
      flash(apiErrorText(err));
    }
  };

  const remove = async (f: FieldT) => {
    setMenu(null);
    if (!window.confirm(t("Удалить поле «{label}»? Его значения будут стёрты из всех карточек контактов.", { label: f.label }))) return;
    try {
      await fieldsApi.remove(f.id);
      setItems((prev) => prev.filter((x) => x.id !== f.id));
      flash(t("Поле «{label}» удалено вместе со значениями в карточках", { label: f.label }));
    } catch (err) {
      flash(apiErrorText(err));
    }
  };

  const openRowMenu = (e: MouseEvent<HTMLButtonElement>, f: FieldT) => {
    const r = e.currentTarget.getBoundingClientRect();
    const width = 190;
    setViewMenu(false);
    setMenu({
      id: f.id,
      top: Math.min(r.bottom + 4, window.innerHeight - 200),
      left: Math.max(8, Math.min(window.innerWidth - width - 8, r.right - width)),
    });
  };

  const sortIcon = (k: SortKey) =>
    sort.key === k ? (sort.dir === 1 ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line") : "ri-arrow-up-down-line";

  const sortBtn = (k: SortKey, label: string) => (
    <button
      type="button"
      onClick={() => toggleSort(k)}
      className="inline-flex cursor-pointer items-center gap-1 whitespace-nowrap text-[11px] font-semibold uppercase tracking-wider text-muted-foreground transition hover:text-foreground"
    >
      {label}
      <i className={cn(sortIcon(k), "text-[13px] leading-none", sort.key === k ? "text-primary" : "opacity-50")} aria-hidden />
    </button>
  );

  const th = "py-2.5 pe-3 text-start font-normal align-middle last:pe-0";
  const rowPad = dense ? "py-1.5" : "py-2.5";
  const menuField = menu ? items.find((f) => f.id === menu.id) : null;
  const draftSystem = Boolean(draft?.id && items.find((f) => f.id === draft.id)?.system);

  return (
    <Band id="fields" tone="default">
      <Head title={title} subtitle={subtitle} />
      <div {...rv()} className="flash-card relative overflow-hidden rounded-card border border-border bg-card !p-0">
        {/* полоска «окна приложения» */}
        <div className="flex flex-wrap items-center gap-2.5 border-b border-border px-4 py-3">
          <span className="flex gap-1.5" aria-hidden>
            <span className="size-2.5 rounded-full bg-foreground/20" />
            <span className="size-2.5 rounded-full bg-foreground/20" />
            <span className="size-2.5 rounded-full bg-foreground/20" />
          </span>
          <span className="flash-mono ms-1 truncate text-[11px] text-muted-foreground">
            {str(brand) || "Hub"} / {t("настройки → входящие → поля контактов")}
          </span>
          <span className="ms-auto inline-flex items-center gap-2 whitespace-nowrap text-[11px] text-muted-foreground">
            <span className="size-1.5 rounded-full bg-primary" aria-hidden />
            {loaded ? t("Полей: {n}", { n: items.length }) : t("Загружаем поля…")}
          </span>
        </div>

        <div className="grid lg:grid-cols-[14.5rem_minmax(0,1fr)]">
          {/* ── настройки ── */}
          <div className="hidden flex-col gap-4 border-border p-3 lg:flex lg:border-e">
            {settingsNav.map((g) => (
              <div key={g.group} className="flex flex-col gap-0.5">
                <p className="px-2 pb-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">{g.group}</p>
                {g.items.map((it) => {
                  const active = it.id === "contact-fields";
                  return (
                    <button
                      key={it.id}
                      type="button"
                      onClick={() => {
                        if (active) return;
                        const anchor = settingsNavAnchors[it.id];
                        if (anchor) {
                          goAnchor(anchor);
                          return;
                        }
                        flash(t("Раздел «{name}» пока не подключён", { name: navLabel(it.id) }));
                      }}
                      aria-current={active ? "page" : undefined}
                      className={cn(
                        "flex cursor-pointer items-center gap-2.5 rounded-btn px-2.5 py-2 text-start text-[12.5px] font-medium transition",
                        active ? "bg-primary/15 text-primary" : "text-muted-foreground hover:bg-muted hover:text-foreground",
                      )}
                    >
                      <i className={cn(it.icon, "text-base leading-none")} aria-hidden />
                      <span className="min-w-0 flex-1 truncate">{it.label}</span>
                    </button>
                  );
                })}
              </div>
            ))}

            <div className="mt-auto rounded-btn border border-border bg-background/60 p-3">
              <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-primary">
                <ShieldCheck className="size-3.5" aria-hidden />
                {t("Доступы")}
              </p>
              <p className="mt-1.5 text-[11px] leading-relaxed text-muted-foreground">
                {t("Поля заводят и меняют владелец и администраторы. Поля «Только админ» другим сотрудникам не показываются.")}
              </p>
            </div>
          </div>

          {/* ── таблица полей ── */}
          <div className="min-w-0 p-3.5 md:p-5">
            <div className="flex flex-col gap-3">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <h3 className="text-base font-semibold leading-snug">{t("Поля контактов")}</h3>
                  <p className="mt-1 max-w-xl text-[12px] leading-relaxed text-muted-foreground">
                    {t("Поля карточки контакта: тип, описание и кто их видит.")}
                  </p>
                </div>
                <div className="flex gap-1.5 overflow-x-auto pb-1 lg:hidden">
                  {settingsNav.flatMap((g) => g.items).map((it) => (
                    <span
                      key={it.id}
                      className={cn(
                        "inline-flex flex-none items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 py-1 text-[11px] font-semibold",
                        it.id === "contact-fields" ? "border-primary bg-primary text-primary-foreground" : "border-border text-muted-foreground",
                      )}
                    >
                      <i className={it.icon} aria-hidden />
                      {it.label}
                    </span>
                  ))}
                </div>
              </div>

              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                {canEdit ? (
                  <button
                    type="button"
                    onClick={openAdd}
                    className="inline-flex h-9 cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-btn bg-primary px-3.5 text-[12px] font-semibold text-primary-foreground transition hover:brightness-110"
                  >
                    <i className="ri-add-line" aria-hidden />
                    {t("Добавить поле")}
                  </button>
                ) : null}
                <div className="relative" data-menu-root>
                  <button
                    type="button"
                    onClick={() => setViewMenu((v) => !v)}
                    aria-expanded={viewMenu}
                    className="inline-flex h-9 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-btn border border-border px-3 text-[12px] font-semibold text-muted-foreground transition hover:text-foreground"
                  >
                    <Settings2 className="size-3.5" aria-hidden />
                    {t("Настроить вид")}
                  </button>
                  {viewMenu ? (
                    <div className="absolute start-0 top-full z-30 mt-1 w-56 rounded-btn border border-border bg-card p-2 shadow-lift">
                      <p className="px-1.5 pb-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{t("Вид таблицы")}</p>
                      <label className="flex cursor-pointer items-center gap-2 rounded-btn px-1.5 py-1.5 text-[12px] hover:bg-muted">
                        <input type="checkbox" checked={showDesc} onChange={() => setShowDesc((v) => !v)} className="size-3.5 cursor-pointer" />
                        {t("Показывать описание")}
                      </label>
                      <label className="flex cursor-pointer items-center gap-2 rounded-btn px-1.5 py-1.5 text-[12px] hover:bg-muted">
                        <input type="checkbox" checked={dense} onChange={() => setDense((v) => !v)} className="size-3.5 cursor-pointer" />
                        {t("Плотные строки")}
                      </label>
                    </div>
                  ) : null}
                </div>
                <label className="relative mt-1 block min-w-0 sm:ms-auto sm:mt-0 sm:w-64">
                  <span className="sr-only">{t("Поиск по полям контактов")}</span>
                  <Search className="pointer-events-none absolute start-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
                  <input
                    type="search"
                    value={query}
                    onChange={(e) => {
                      setQuery(e.target.value);
                      setPage(1);
                    }}
                    placeholder={t("Найти поле: имя, телефон, город…")}
                    className="h-9 w-full box-border rounded-btn border border-border bg-background ps-9 pe-3 text-[13px] text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/25"
                  />
                </label>
              </div>
            </div>

            <div className="mt-3 overflow-x-auto">
              <table className="w-full min-w-[620px] border-collapse text-start">
                <thead>
                  <tr className="border-b border-border">
                    <th scope="col" className={th}>{sortBtn("label", t("Название"))}</th>
                    <th scope="col" className={cn(th, "hidden sm:table-cell")}>{sortBtn("key", t("ID поля"))}</th>
                    {showDesc ? <th scope="col" className={cn(th, "hidden lg:table-cell")}>{t("Описание")}</th> : null}
                    <th scope="col" className={th}>{sortBtn("type", t("Тип"))}</th>
                    <th scope="col" className={cn(th, "hidden sm:table-cell")}>{t("Видимость")}</th>
                    <th scope="col" className={cn(th, "hidden md:table-cell")}>{sortBtn("added", t("Добавлено"))}</th>
                    <th scope="col" className="w-10 py-2.5 text-end">
                      <span className="sr-only">{t("Действия")}</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((f) => {
                    const ft = typeOf(f.type);
                    const v = visOf(f.visibility);
                    return (
                      <tr key={f.id} className="border-b border-border/60 transition last:border-0 hover:bg-muted/40">
                        <td className={cn(rowPad, "pe-3")}>
                          <div className="flex items-center gap-2">
                            {canEdit ? (
                              <button
                                type="button"
                                onClick={() => openEdit(f)}
                                className="cursor-pointer text-start text-[13px] font-semibold leading-snug hover:text-primary"
                              >
                                {f.label}
                              </button>
                            ) : (
                              <span className="text-[13px] font-semibold leading-snug">{f.label}</span>
                            )}
                            {f.system ? (
                              <span className="hidden rounded-full border border-border px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-muted-foreground sm:inline-flex">
                                {t("системное")}
                              </span>
                            ) : null}
                          </div>
                        </td>
                        <td className={cn(rowPad, "hidden pe-3 sm:table-cell")}>
                          <code className="flash-mono text-[11px] text-muted-foreground">{f.key}</code>
                        </td>
                        {showDesc ? (
                          <td className={cn(rowPad, "hidden pe-4 lg:table-cell")}>
                            <span className="line-clamp-1 max-w-[18rem] text-[12px] text-muted-foreground">{f.description || "—"}</span>
                          </td>
                        ) : null}
                        <td className={cn(rowPad, "pe-3")}>
                          <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-border bg-background/70 px-2 py-1 text-[11px] font-medium text-foreground/80">
                            <i className={cn(ft.icon, "text-[13px] leading-none")} aria-hidden />
                            {ft.label}
                          </span>
                        </td>
                        <td className={cn(rowPad, "hidden pe-3 sm:table-cell")}>
                          <span
                            className={cn(
                              "inline-flex items-center gap-1.5 whitespace-nowrap text-[12px]",
                              f.visibility === "always" ? "text-muted-foreground" : "text-primary",
                            )}
                          >
                            <i className={cn(v.icon, "text-[13px] leading-none")} aria-hidden />
                            {v.label}
                          </span>
                        </td>
                        <td className={cn(rowPad, "hidden whitespace-nowrap pe-3 text-[12px] tabular-nums text-muted-foreground md:table-cell")}>
                          {formatDate(f.createdAt)}
                        </td>
                        <td className={cn(rowPad, "text-end")}>
                          {canEdit ? (
                            <div className="relative inline-block" data-menu-root>
                              <button
                                type="button"
                                onClick={(e) => (menu?.id === f.id ? setMenu(null) : openRowMenu(e, f))}
                                aria-label={t("Действия с полем {label}", { label: f.label })}
                                aria-expanded={menu?.id === f.id}
                                className="grid size-7 cursor-pointer place-items-center rounded-btn text-muted-foreground transition hover:bg-muted hover:text-foreground"
                              >
                                <MoreVertical className="size-4" aria-hidden />
                              </button>
                            </div>
                          ) : null}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {loaded && rows.length === 0 ? (
                <p className="py-10 text-center text-sm text-muted-foreground">
                  {t("Поле не найдено. Попробуйте другое название или добавьте новое.")}
                </p>
              ) : null}
            </div>

            <div className="mt-3 flex flex-wrap items-center justify-between gap-3 border-t border-border/60 pt-3 text-[11px] text-muted-foreground">
              <label className="inline-flex items-center gap-2">
                {t("Полей на странице")}
                <span className="relative inline-flex items-center">
                  <select
                    value={perPage}
                    onChange={(e) => {
                      setPerPage(Number(e.target.value));
                      setPage(1);
                    }}
                    aria-label={t("Полей на странице")}
                    className="h-8 cursor-pointer appearance-none rounded-btn border border-border bg-background ps-2.5 pe-7 text-[11px] font-semibold text-foreground focus:border-primary focus:outline-none"
                  >
                    {[10, 25, 50].map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="pointer-events-none absolute end-1.5 size-3.5" aria-hidden />
                </span>
              </label>
              <div className="flex items-center gap-2">
                <span className="tabular-nums">{range}</span>
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={current === 1}
                  aria-label={t("Предыдущая страница")}
                  className="grid size-7 place-items-center rounded-btn border border-border text-muted-foreground transition hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <i className="ri-arrow-left-s-line" aria-hidden />
                </button>
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.min(pages, p + 1))}
                  disabled={current >= pages}
                  aria-label={t("Следующая страница")}
                  className="grid size-7 place-items-center rounded-btn border border-border text-muted-foreground transition hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <i className="ri-arrow-right-s-line" aria-hidden />
                </button>
              </div>
            </div>

            <p className="mt-3 text-[11px] text-muted-foreground">
              {canEdit
                ? t("Изменения сохраняются сразу. Удаление поля стирает его значения во всех карточках.")
                : t("У вас только просмотр: поля меняют владелец и администраторы.")}
            </p>
          </div>
        </div>

        {toast ? (
          <div
            role="status"
            className="pointer-events-none absolute inset-x-3 bottom-3 z-40 mx-auto flex max-w-md items-start gap-2 rounded-btn border border-primary/40 bg-background/95 px-3.5 py-2.5 text-[12px] text-foreground shadow-lift backdrop-blur"
          >
            <Check className="mt-0.5 size-3.5 flex-none text-primary" strokeWidth={3} aria-hidden />
            {toast}
          </div>
        ) : null}

        {/* ── меню действий над строкой ── */}
        {menu && menuField ? (
          <div
            role="menu"
            data-menu-root
            style={{ top: menu.top, left: menu.left }}
            className="fixed z-50 w-[190px] overflow-hidden rounded-btn border border-border bg-card p-1 shadow-lift"
          >
            <button
              type="button"
              role="menuitem"
              onClick={() => openEdit(menuField)}
              className="flex w-full cursor-pointer items-center gap-2 rounded-btn px-2.5 py-2 text-start text-[12px] font-medium transition hover:bg-muted"
            >
              <i className="ri-pencil-line text-[14px] leading-none text-muted-foreground" aria-hidden />
              {t("Изменить поле")}
            </button>
            <button
              type="button"
              role="menuitem"
              onClick={() => void duplicate(menuField)}
              className="flex w-full cursor-pointer items-center gap-2 rounded-btn px-2.5 py-2 text-start text-[12px] font-medium transition hover:bg-muted"
            >
              <i className="ri-file-copy-2-line text-[14px] leading-none text-muted-foreground" aria-hidden />
              {t("Дублировать")}
            </button>
            <button
              type="button"
              role="menuitem"
              onClick={() => void cycleVisibility(menuField)}
              className="flex w-full cursor-pointer items-center gap-2 rounded-btn px-2.5 py-2 text-start text-[12px] font-medium transition hover:bg-muted"
            >
              <i className="ri-eye-line text-[14px] leading-none text-muted-foreground" aria-hidden />
              {t("Сменить видимость")}
            </button>
            <button
              type="button"
              role="menuitem"
              onClick={() => void remove(menuField)}
              disabled={menuField.system}
              title={menuField.system ? t("Встроенное поле нельзя удалить — его можно скрыть") : undefined}
              className="flex w-full items-center gap-2 rounded-btn px-2.5 py-2 text-start text-[12px] font-medium text-red-500 transition hover:bg-red-500/10 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <i className="ri-delete-bin-line text-[14px] leading-none" aria-hidden />
              {t("Удалить поле")}
            </button>
          </div>
        ) : null}

        {/* ── окно добавления / правки поля ── */}
        {draft ? (
          <div className="absolute inset-0 z-[60] flex items-end justify-center bg-black/65 p-3 backdrop-blur-sm sm:items-center md:p-6">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                void save();
              }}
              role="dialog"
              aria-modal="true"
              aria-labelledby="field-dialog-title"
              className="relative max-h-full w-full max-w-lg overflow-y-auto rounded-card border border-border bg-card p-5 shadow-lift md:p-6"
            >
              <button
                type="button"
                onClick={() => setDraft(null)}
                aria-label={t("Закрыть окно поля")}
                className="absolute end-3 top-3 grid size-8 cursor-pointer place-items-center rounded-btn text-muted-foreground transition hover:bg-muted hover:text-foreground"
              >
                <X className="size-4" aria-hidden />
              </button>

              <div className="flex items-start gap-3 pe-8">
                <span className="grid size-11 flex-none place-items-center rounded-btn bg-primary/10 text-2xl leading-none text-primary" aria-hidden>
                  <i className="ri-contacts-book-2-line" />
                </span>
                <div className="min-w-0">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                    {draft.id ? t("Настройка поля") : t("Новое поле")}
                  </p>
                  <h3 id="field-dialog-title" className="mt-1 text-lg font-semibold leading-snug">
                    {draft.id ? t("Поле «{label}»", { label: draft.label || t("без названия") }) : t("Добавить поле контакта")}
                  </h3>
                </div>
              </div>

              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                <label className="flex min-w-0 flex-col gap-1.5 sm:col-span-2">
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{t("Название")}</span>
                  <input
                    value={draft.label}
                    onChange={(e) => {
                      const label = e.target.value;
                      setDraft((d) => d && { ...d, label, key: keyTouched ? d.key : slug(label) });
                    }}
                    placeholder={t("Например, Город доставки")}
                    autoComplete="off"
                    maxLength={60}
                    className={FIELD}
                  />
                </label>

                <label className="flex min-w-0 flex-col gap-1.5">
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{t("ID поля")}</span>
                  <input
                    value={draft.key}
                    readOnly={Boolean(draft.id)}
                    title={draft.id ? t("ID поля после создания не меняется") : undefined}
                    onChange={(e) => {
                      setKeyTouched(true);
                      setDraft((d) => d && { ...d, key: e.target.value.replace(/\s+/g, "") });
                    }}
                    placeholder="city"
                    autoComplete="off"
                    spellCheck={false}
                    className={cn(FIELD, "flash-mono", draft.id && "cursor-not-allowed opacity-60", keyError && "border-red-400 focus:border-red-400 focus:ring-red-400/25")}
                  />
                </label>

                <label className="flex min-w-0 flex-col gap-1.5">
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{t("Тип")}</span>
                  <span className="relative flex items-center">
                    <select
                      value={draft.type}
                      onChange={(e) => setDraft((d) => d && { ...d, type: e.target.value as FieldType })}
                      disabled={draftSystem}
                      className={cn(FIELD, "cursor-pointer appearance-none pe-8 disabled:cursor-not-allowed disabled:opacity-60")}
                    >
                      {fieldTypes.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.label}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="pointer-events-none absolute end-2.5 size-3.5 text-muted-foreground" aria-hidden />
                  </span>
                </label>

                <label className="flex min-w-0 flex-col gap-1.5 sm:col-span-2">
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{t("Описание")}</span>
                  <input
                    value={draft.description}
                    onChange={(e) => setDraft((d) => d && { ...d, description: e.target.value })}
                    placeholder={t("Что здесь хранится и зачем продавцу")}
                    autoComplete="off"
                    maxLength={200}
                    className={FIELD}
                  />
                </label>

                {draft.type === "multi" ? (
                  <label className="flex min-w-0 flex-col gap-1.5 sm:col-span-2">
                    <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{t("Варианты")}</span>
                    <input
                      value={draft.options}
                      onChange={(e) => setDraft((d) => d && { ...d, options: e.target.value })}
                      placeholder={t("Через запятую: опт, розница, дилер")}
                      autoComplete="off"
                      className={FIELD}
                    />
                  </label>
                ) : null}

                <label className="flex min-w-0 flex-col gap-1.5 sm:col-span-2">
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{t("Видимость")}</span>
                  <span className="relative flex items-center">
                    <select
                      value={draft.visibility}
                      onChange={(e) => setDraft((d) => d && { ...d, visibility: e.target.value as Visibility })}
                      className={cn(FIELD, "cursor-pointer appearance-none pe-8")}
                    >
                      {fieldVisibility.map((v) => (
                        <option key={v.id} value={v.id}>
                          {v.label}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="pointer-events-none absolute end-2.5 size-3.5 text-muted-foreground" aria-hidden />
                  </span>
                </label>
              </div>

              {keyError ? <p className="mt-2 text-[11px] text-red-500">{keyError}</p> : null}

              <p className="mt-3 flex items-start gap-2 rounded-btn border border-border bg-background/60 px-3 py-2.5 text-[11px] leading-relaxed text-muted-foreground">
                <ShieldCheck className="mt-0.5 size-3.5 flex-none text-primary" aria-hidden />
                {draftSystem
                  ? t("Встроенное поле: название, описание и видимость можно менять; удалить и сменить тип нельзя — на нём держится карточка контакта.")
                  : t("Поле появится в карточке контакта, в импорте и выгрузке CSV. ID поля после создания не меняется.")}
              </p>

              <div className="mt-4 flex flex-col-reverse items-stretch gap-2 sm:flex-row sm:items-center sm:justify-end">
                <button
                  type="button"
                  onClick={() => setDraft(null)}
                  className="inline-flex h-10 cursor-pointer items-center justify-center whitespace-nowrap rounded-btn border border-border px-4 text-[13px] font-semibold text-muted-foreground transition hover:text-foreground"
                >
                  {t("Отмена")}
                </button>
                <button
                  type="submit"
                  disabled={!canSave}
                  className="inline-flex h-10 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-btn bg-primary px-4 text-[13px] font-semibold text-primary-foreground transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Check className="size-4" aria-hidden />
                  {busy ? t("Сохраняем…") : draft.id ? t("Сохранить") : t("Добавить поле")}
                </button>
              </div>
            </form>
          </div>
        ) : null}
      </div>
    </Band>
  );
}

export default FieldsDemo;